/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import styled from "styled-components"
import Headroom from "react-headroom"
import Animated from "animated/lib/targets/react-dom"
import { scrollDispatch, SmoothScrollLink } from "organism-react-scroll-nav"
import MenuItem from "./menu-item"
import Logo from "./logo"
import Content from "./content"

import IconFontTTF from "../fonts/icomoon.ttf"
import IconFontWOFF from "../fonts/icomoon.woff"

import Header from "./header"
import Home from "./home"
import StickyHeader from "./sticky-header"
import "./layout.css"
import { useScroll } from "../utils/useScroll"

const Footer = styled.footer`
  width: 100%;
  height: 560px;
  position: relative;
  display: flex;
  justify-content: center;

  @media (max-width: 415px) {
    height: 360px !important;
  }

  ${Content} {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;

    @media (max-width: 900px) {
      position: relative;
      ul {
        position: static;
        padding: 25px;
        flex-direction: column;

        li {
          height: auto;
          margin: 5px 0;
          font-size: 14px;
        }

        li:first-child {
          left: auto;
          top: auto;
          right: 80px;
          bottom: 90px;
          transform-origin: 100% 100%;
        }
      }
    }
  }

  ul {
    position: relative;
    display: flex;
    margin: 0;
    padding-left: 140px;

    li:first-child {
      position: absolute;
      left: 35px;
      top: 30px;
      transform: scale(0.22);
      transform-origin: 0 0;
    }

    li {
      height: 125px;
    }
  }
`

const Social = styled.div`
  @font-face {
    font-family: 'icomoon';
    src:  url('${IconFontTTF}') format('truetype'),
      url('${IconFontWOFF}') format('woff');
    font-weight: normal;
    font-style: normal;
  }

  position: relative;
  display: flex;
  padding: 24px 45px;

  @media (max-width: 900px) {
    padding: 24px;

    a {
      margin: 12px !important;
    }
  }

  a {
    font-family: 'icomoon' !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    line-height: 1;

    /* Better Font Rendering =========== */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .icon-facebook:before {
    content: "\\e900";
  }
  .icon-instagram:before {
    content: "\\e901";
  }
  .icon-youtube:before {
    content: "\\e902";
  }

  a {
    display: block;
    font-size: 30px;
    color: #ff824e;
    text-decoration: none;
    margin: 18px;
  }
`

const FooterBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;

  @media (max-width: 415px) {
    &:after {
      height: 240px !important;
    }
  }

  @media (max-width: 900px) {
    &:after {
      content: "";
      position: absolute;
      width: 100%;
      height: 250px;
      left: 0;
      bottom: 0;
      background: #515151;
    }
  }

  svg {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 1920px;

    @media (max-width: 415px) {
      margin-top: -200px !important;
    }
    @media (max-width: 900px) {
      margin-top: -70px;
      width: 102%;
    }
  }
`

const StyledHeadroom = styled(Headroom)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;

  svg {
    opacity: 0;
  }

  .headroom--scrolled {
    background: url(${({ url }) => url});
    background-position: 50% 0;
    -background: linear-gradient
    backdrop-filter: blur(30px);

    svg {
      opacity: 1;
    }
  }
  
`

const Layout = ({ children, tagToggle }) => {
  const scrollTo = useScroll()

  const data = useStaticQuery(graphql`
    query LayoutData {
      site {
        siteMetadata {
          title
          menu
        }
      }
      placeholderImage: file(relativePath: { eq: "background.png" }) {
        childImageSharp {
          fluid(maxWidth: 1920) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)

  return (
    <>
      <StickyHeader url={data.placeholderImage.childImageSharp.fluid.src}>
        <Header />
      </StickyHeader>
      <Home id="За нас" />
      <main>{children}</main>
      <Footer>
        <FooterBackground>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1920.016"
            height="563.096"
            viewBox="0 0 1920.016 563.096"
          >
            <g transform="translate(0.016 -6388.905)">
              <path
                d="M1894,592.1H-26v-258.6c.131.045,14.645,5.135,41.069,12.888,15.317,4.5,31.494,8.96,48.082,13.272,20.676,5.374,42.053,10.52,63.537,15.3,1.8.844,3.455,1.614,5.056,2.355,56.338,26.068,113.445,46.837,169.734,61.733,16.282,4.308,32.7,8.173,48.8,11.488,16.954,3.49,34.037,6.47,50.776,8.856,17.557,2.5,35.209,4.421,52.466,5.7,18.047,1.338,36.171,2.017,53.869,2.017,20.068,0,40.165-.873,59.733-2.594,1.482-.13,3-.269,4.507-.412,18.918-1.8,37.823-4.425,56.188-7.816a625.3,625.3,0,0,0,61.27-14.535,618.872,618.872,0,0,0,61.485-21.516c6.871-2.821,13.749-5.676,20.443-8.488,16.815-7.063,33.105-14.11,48.416-20.947,5.241-2.34,10.5-4.716,15.635-7.06,6.4-2.92,12.77-5.871,18.942-8.772,29.219-6.515,56.558-13.079,81.259-19.511,22.146-5.766,42.5-11.493,62.221-17.507,36.438-11.113,63.945-21.165,90.548-30.885l.008,0c30.885-11.286,60.057-21.947,100.01-33.112,21.372-5.973,44.069-11.6,69.386-17.2,28.186-6.237,59.058-12.325,94.381-18.613,18.615-3.313,37.593-6.221,56.406-8.645,18.031-2.325,36.4-4.269,54.6-5.78,33.869-2.814,68.442-4.24,102.76-4.24a1269.082,1269.082,0,0,1,133.462,7.064c35.317,3.751,70.228,8.98,103.764,15.542,29.841,5.84,53.045,11.675,67.255,15.542,15.4,4.192,23.852,7.037,23.933,7.065V592.1ZM958.548,320.668v0c3.54-2.055,7.087-4.142,10.543-6.2,15.6-9.3,30.354-18.649,45.109-28.584-5.519,3.719-11.141,7.42-16.709,11C984.755,305.072,972.017,312.853,958.548,320.668Zm83.245-54.066h0c8.189-5.926,16.925-12.424,26.706-19.865C1062.226,251.51,1052.632,258.762,1041.793,266.6Z"
                transform="translate(26 6359.904)"
                fill="#515151"
              />
              <path
                d="M-2255.589,1022.1a605.06,605.06,0,0,1-70.024-4.094,622.267,622.267,0,0,1-63.448-10.8,638.418,638.418,0,0,1-103.763-32.779c-13.174-5.438-26.166-11.328-38.614-17.508-9.954-4.942-19.588-10.081-28.635-15.273-15.376-8.827-23.845-14.841-23.929-14.9V870.614c.063.01,8.59,1.448,23.933,3.54,14.216,1.938,37.424,4.863,67.256,7.788,33.534,3.288,68.445,5.909,103.765,7.789,44.15,2.349,89.053,3.54,133.461,3.54,34.313,0,68.887-.715,102.76-2.125,37.271-1.551,74.618-3.984,111-7.229,132.381-11.809,190.586-21.305,243.216-30.8-16.394,12.441-29.569,21.955-42.719,30.848-16.527,11.178-33.061,21.37-50.545,31.16-19.993,11.2-41.162,21.845-64.717,32.557-12.508,5.689-25.85,11.456-39.656,17.142-14.342,5.907-29.677,11.929-45.579,17.9-18.578,6.975-37.554,13.109-56.4,18.232-18.018,4.9-36.389,9-54.6,12.189-17.36,3.041-35.038,5.336-52.543,6.82C-2221.986,1021.38-2238.883,1022.095-2255.589,1022.1ZM-1798.6,853.122v0c6.892-5.23,13.835-10.581,20.549-15.756l.008-.006c16.433-12.664,33.424-25.759,52.627-39.439,21.679-15.445,43.375-29.6,66.33-43.288,27.746-16.54,57.584-32.582,91.217-49.043,18.316-8.964,37.978-18.141,58.44-27.278,21.557-9.626,44.725-19.529,68.859-29.435a618.453,618.453,0,0,1,61.484-21.515,625.555,625.555,0,0,1,61.275-14.537c19.852-3.665,40.271-6.434,60.692-8.229,19.576-1.721,39.673-2.594,59.733-2.594,17.7,0,35.821.679,53.868,2.017,17.261,1.28,34.913,3.2,52.466,5.7,16.729,2.385,33.813,5.365,50.776,8.856,16.11,3.317,32.529,7.182,48.8,11.487,56.29,14.9,113.4,35.666,169.734,61.733,22.132,10.24,44.2,21.334,65.582,32.971,17.111,9.312,33.825,18.989,49.676,28.762,27.035,16.668,42.335,27.948,42.486,28.06v54.538c-.127-.022-15.316-2.686-42.49-6.667-24.917-3.65-65.141-9.157-115.266-14.668-56.337-6.193-113.445-11.128-169.736-14.668-70.365-4.424-139.639-6.667-205.9-6.667-41.017,0-81.533.865-120.423,2.57-42.116,1.848-83.419,4.73-122.76,8.567-88.806,8.661-160.284,16.88-218.518,25.129-51.279,7.264-85.689,13.565-118.965,19.658C-1784.429,850.545-1791.651,851.868-1798.6,853.122Z"
                transform="translate(-664 7429) rotate(180)"
                fill="#ff824e"
                opacity="0.2"
              />
              <path
                d="M-663.984,1040.095H-664V781.587c-.143-.105-15.045-11.077-41.068-27.183-15.338-9.492-31.515-18.91-48.083-27.991-20.71-11.352-42.093-22.209-63.557-32.269,21.483,4.774,42.867,9.923,63.557,15.3,16.571,4.307,32.748,8.772,48.083,13.272,26.124,7.666,40.922,12.837,41.068,12.889l.016,304.489Zm-1591.6-18a604.967,604.967,0,0,1-69.73-4.06,622.15,622.15,0,0,1-63.215-10.717,638.434,638.434,0,0,1-103.526-32.582c-13.173-5.417-26.171-11.289-38.632-17.453-9.995-4.944-19.671-10.088-28.759-15.287-15.6-8.925-24.181-14.995-24.541-15.251l.006,88.169h0l-.012-88.174.008.006L-2584,804.429c.067.022,8.4,2.838,23.932,7.065,14.21,3.867,37.412,9.7,67.255,15.542,33.538,6.563,68.449,11.792,103.764,15.542a1268.968,1268.968,0,0,0,133.461,7.064c34.317,0,68.891-1.426,102.761-4.24,18.213-1.513,36.583-3.458,54.6-5.78,18.839-2.428,37.817-5.337,56.406-8.646,35.331-6.289,66.2-12.377,94.38-18.612,25.311-5.6,48.008-11.228,69.386-17.2,39.941-11.162,69.119-21.824,100.009-33.112l.009,0c26.611-9.724,54.127-19.778,90.548-30.885,19.689-6,40.042-11.731,62.221-17.507,24.7-6.431,52.038-13,81.26-19.51-29.211,13.734-56.551,27.577-81.26,41.147-22.173,12.177-42.526,24.255-62.221,36.922-36.42,23.425-63.936,44.63-90.547,65.137l-.01.008c-30.891,23.807-60.068,46.293-100.01,69.835-21.38,12.6-44.076,24.469-69.386,36.281-13.64,6.365-28.275,12.811-43.5,19.159-15.905,6.631-33.024,13.392-50.88,20.1-18.582,6.976-37.557,13.11-56.4,18.232-18.016,4.9-36.387,9-54.6,12.189-17.364,3.042-35.042,5.336-52.543,6.82C-2221.989,1021.38-2238.885,1022.095-2255.588,1022.1Zm711.58-327.952v0c31.294-14.711,66.1-29.941,103.436-45.267a618.588,618.588,0,0,1,61.486-21.516,625.449,625.449,0,0,1,61.27-14.535c19.846-3.664,40.264-6.433,60.686-8.228,19.574-1.721,39.672-2.593,59.735-2.593,18,0,36.434.7,54.795,2.086,17.547,1.323,35.492,3.3,53.337,5.889,17,2.463,34.355,5.537,51.578,9.138,16.347,3.417,33.008,7.4,49.519,11.833,56.911,15.284,114.6,36.545,171.45,63.192-56.849-12.631-114.533-22.712-171.452-29.96-33.938-4.321-67.953-7.667-101.1-9.944-36.534-2.51-72.913-3.782-108.126-3.782-20.068,0-40.167.414-59.738,1.229-20.418.851-40.835,2.163-60.682,3.9-20.651,1.808-41.264,4.126-61.266,6.892-20.765,2.871-41.453,6.3-61.49,10.2C-1477.927,679.949-1512.728,687.171-1544.009,694.144Z"
                transform="translate(-664 7429) rotate(180)"
                fill="#ff824e"
                opacity="0.4"
              />
            </g>
          </svg>
        </FooterBackground>
        <Content>
          <ul>
            <li>
              <Logo animation={new Animated.Value(0)} />
            </li>
            {data.site.siteMetadata.menu.map(item => (
              <MenuItem key={item} onClick={() => scrollTo(item)}>
                {item}
              </MenuItem>
            ))}
          </ul>
        </Content>
      </Footer>
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
